// Platform Orgs — Enable / Disable Stream chat for a selected organization.
import { useCallback, useEffect, useState } from "react";
import {
  fetchWorkplaceChatVendorConfig,
  patchWorkplaceChatVendorConfig,
  type WorkplaceChatVendorConfigStatus,
} from "@/lib/platform/workplaceChatVendorConfigClient";

export type PlatformPanelOrgChatControlsProps = {
  orgId: string;
  orgName: string;
};

export function PlatformPanelOrgChatControls({ orgId, orgName }: PlatformPanelOrgChatControlsProps) {
  const [status, setStatus] = useState<WorkplaceChatVendorConfigStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>("");

  const reload = useCallback(async () => {
    setLoading(true);
    setMsg("");
    const r = await fetchWorkplaceChatVendorConfig(orgId);
    setLoading(false);
    if (!r.ok) {
      setStatus(null);
      setMsg(r.error);
      return;
    }
    setStatus(r);
  }, [orgId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onToggle = async (enabled: boolean) => {
    setBusy(true);
    setMsg("");
    const r = await patchWorkplaceChatVendorConfig({
      organizationId: orgId,
      vendorEnabled: enabled,
    });
    setBusy(false);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setStatus(r);
    setMsg(
      enabled
        ? `Chat enabled for ${orgName}. Ensure Stream credentials are set and back-end channel/user records exist.`
        : `Chat disabled for ${orgName}.`
    );
  };

  const enabled = status?.vendor_enabled === true;
  const effective = status?.effectively_enabled === true;

  return (
    <div className="px-4 py-4 border-t border-teal-100 bg-teal-50/70 dark:border-teal-900 dark:bg-teal-950/25">
      <div className="text-sm font-semibold text-teal-900 dark:text-teal-100">
        Team chat (Stream)
      </div>
      <p className="mt-1 text-xs text-teal-900/80 dark:text-teal-100/80">
        Developer toggle for <span className="font-medium">{orgName}</span>. Writes{" "}
        <span className="font-mono">ops_org_settings.workplace_chat_config.vendor_enabled</span>.
      </p>

      {loading ? (
        <p className="mt-2 text-xs text-gray-500">Loading chat status…</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span
            className={
              "text-xs px-2 py-0.5 rounded-full font-medium " +
              (effective
                ? "bg-teal-600 text-white"
                : enabled
                  ? "bg-amber-100 text-amber-900"
                  : "bg-gray-200 text-gray-700 dark:bg-zinc-700 dark:text-zinc-200")
            }
          >
            {effective
              ? "Effectively on"
              : enabled
                ? "Flag on (creds missing?)"
                : "Off"}
          </span>
          {status && !status.stream_credentials_configured ? (
            <span className="text-xs text-amber-800 dark:text-amber-200">
              Stream env keys not configured on server
            </span>
          ) : null}
          <button
            type="button"
            disabled={busy || enabled}
            onClick={() => void onToggle(true)}
            className="px-3 py-1.5 rounded-lg bg-teal-700 text-white text-xs font-medium hover:bg-teal-800 disabled:opacity-50"
          >
            Enable chat
          </button>
          <button
            type="button"
            disabled={busy || !enabled}
            onClick={() => void onToggle(false)}
            className="px-3 py-1.5 rounded-lg border border-teal-300 bg-white text-teal-900 text-xs font-medium hover:bg-teal-100 disabled:opacity-50 dark:border-teal-700 dark:bg-zinc-900 dark:text-teal-100"
          >
            Disable chat
          </button>
          <button
            type="button"
            disabled={busy || loading}
            onClick={() => void reload()}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-xs hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-200"
          >
            Refresh
          </button>
        </div>
      )}
      {msg ? (
        <p className="mt-2 text-xs text-teal-950 dark:text-teal-100">{msg}</p>
      ) : null}
    </div>
  );
}
